import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { getAllCompositeSegments, getClipFPS } from '@goldcast/player-config/src/utils/clipUtils';
import { remotionPlayerStateStore } from '../../ClipPlayer/ClipNewTimeline/store/remotionPlayerState';
import { getSegmentTimeFromPlayerTime } from '../../ClipPlayer/ClipNewTimeline/timeUtils';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import featureFlagStore from '@/stores/featureFlagStore';
import { FeatureFlagKeys } from '@/services/featureFlag';
import { getFrameAsPng } from '@/libs/getVideoFrame';

export function useTemplateThumbnails() {
  const enabled = !!featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Better_Template_Thumbnails];
  const { clipData } = useClipsContext();

  const { currentTime } = useSyncExternalStore(
    remotionPlayerStateStore.subscribe,
    remotionPlayerStateStore.getSnapshot
  );

  const compositeSegment = useMemo(() => {
    const segments = getAllCompositeSegments(clipData);
    if (!segments.length) {
      return undefined;
    }
    const currentPlayerFrame = currentTime * getClipFPS(clipData);
    return segments.find(s => currentPlayerFrame >= s.fromFrame && currentPlayerFrame <= s.toFrame) ?? segments[0];
  }, [clipData, currentTime]);

  const absoluteSourceTime = useMemo(() => {
    if (!compositeSegment) {
      return 0;
    }
    // Clamp to 0: cursor on intro/outro maps to a negative source time (playerTime < segment offset).
    // No upper clamp needed — mediabunny returns the last frame for any time beyond the video end.
    return Math.max(0, getSegmentTimeFromPlayerTime(currentTime, clipData, compositeSegment.id));
  }, [currentTime, clipData, compositeSegment]);

  const previewUrlsKey = useMemo(() => {
    if (!compositeSegment) {
      return '';
    }
    // Deduplicate: uploads share one source file so all virtual speakers have the same preview URL,
    // which would cause redundant fetches. Unique URLs ensure one fetch per actual source file.
    const unique = [...new Set(compositeSegment.videos.filter(v => v.details.preview).map(v => v.details.preview))];
    return unique.join('|');
  }, [compositeSegment]);

  const [thumbnailMap, setThumbnailMap] = useState<Record<string, string>>({});
  const objectUrlsRef = useRef<string[]>([]);

  useEffect(() => {
    if (!previewUrlsKey || !enabled) {
      return;
    }
    const urls = previewUrlsKey.split('|');
    let cancelled = false;

    const timeoutId = setTimeout(async () => {
      try {
        const blobs = await Promise.all(urls.map(url => getFrameAsPng(url, absoluteSourceTime)));
        if (cancelled) {
          return;
        }
        objectUrlsRef.current.forEach(url => URL.revokeObjectURL(url));
        const newObjectUrls = blobs.map(blob => URL.createObjectURL(blob));
        objectUrlsRef.current = newObjectUrls;
        setThumbnailMap(Object.fromEntries(urls.map((url, i) => [url, newObjectUrls[i]])));
      } catch (e) {
        console.warn('template thumbnail decode error', e);
      }
    }, 200);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [previewUrlsKey, absoluteSourceTime, enabled]);

  useEffect(() => {
    return () => objectUrlsRef.current.forEach(url => URL.revokeObjectURL(url));
  }, []);

  return { thumbnailMap, absoluteSourceTime, currentTime };
}
